/**
 * Landing hero table demo — full deal + pro seats + UTG+1 raise / BTN raise / BB call.
 * CSS timeline only (no video). Pros from Calling Pros icon pack.
 */
import type { CSSProperties } from "react";
import { BRAND } from "@shared/brand";
import { CALLING_PROS } from "@shared/calling-pros";

/** 6-max layout (ellipse around felt). Roles for action script. */
const SEATS: Array<{
  left: string;
  top: string;
  role: "BTN" | "SB" | "BB" | "UTG" | "UTG1" | "CO";
  label: string;
  proIdx: number;
  /** Hole card faces after deal (board uses backs until optional later) */
  hole: [string, string];
}> = [
  {
    left: "50%",
    top: "86%",
    role: "BTN",
    label: "BTN",
    proIdx: 7, // David BetUDontBet
    hole: ["/cards2/Spades_A.png", "/cards2/Spades_Q.png"],
  },
  {
    left: "22%",
    top: "78%",
    role: "SB",
    label: "SB",
    proIdx: 1, // Phil Ivey
    hole: ["/cards2/Hearts_7.png", "/cards2/Club_2.png"],
  },
  {
    left: "12%",
    top: "48%",
    role: "BB",
    label: "BB",
    proIdx: 2, // Daniel Negreanu
    hole: ["/cards2/Diamonds_Q.png", "/cards2/Diamonds_J.png"],
  },
  {
    left: "22%",
    top: "22%",
    role: "UTG",
    label: "UTG",
    proIdx: 3, // Phil Hellmuth
    hole: ["/cards2/Club_9.png", "/cards2/Hearts_3.png"],
  },
  {
    left: "78%",
    top: "22%",
    role: "UTG1",
    label: "UTG+1",
    proIdx: 9, // Tom Dwan
    hole: ["/cards2/Spades_A.png", "/cards2/Diamonds_A.png"],
  },
  {
    left: "88%",
    top: "48%",
    role: "CO",
    label: "CO",
    proIdx: 14, // Fedor Holz
    hole: ["/cards2/Hearts_K.png", "/cards2/Club_Q.png"],
  },
];

const BOARD = [
  "/cards2/Hearts_A.png",
  "/cards2/Spades_10.png",
  "/cards2/Club_7.png",
  "/cards2/Diamonds_2.png",
  "/cards2/Hearts_9.png",
];

export function LandingTableDemo() {
  return (
    <div className="landing-table-demo" aria-hidden>
      <div
        className="landing-table-demo__felt"
        style={{ backgroundImage: `url(${BRAND.felts.green})` }}
      />
      <div className="landing-table-demo__rail" />

      {/* Dealer puck near BTN */}
      <div className="landing-table-demo__dealer" title="Dealer">
        D
      </div>

      {/* Community board — large cards, full flop→turn→river after action */}
      <div className="landing-table-demo__board">
        {BOARD.map((src, i) => (
          <div
            key={i}
            className="landing-table-demo__card landing-table-demo__card--board"
            style={
              {
                ["--i" as string]: i,
                backgroundImage: `url(${src})`,
              } as CSSProperties
            }
          />
        ))}
      </div>

      {/* Pot + stack growing with raises */}
      <div className="landing-table-demo__pot">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <span
            key={i}
            className="landing-table-demo__chip"
            style={
              {
                ["--i" as string]: i,
                backgroundImage: `url(/chips/${i < 3 ? 25 : i < 6 ? 100 : 500}.png)`,
              } as CSSProperties
            }
          />
        ))}
        <div className="landing-table-demo__pot-amount">
          <span className="landing-table-demo__pot-n">POT</span>
          <span className="landing-table-demo__pot-v" />
        </div>
      </div>

      {/* Action callouts */}
      <div className="landing-table-demo__action landing-table-demo__action--utg1">
        UTG+1 · RAISE
      </div>
      <div className="landing-table-demo__action landing-table-demo__action--btn">
        BTN · RAISE
      </div>
      <div className="landing-table-demo__action landing-table-demo__action--bb">
        BB · CALL
      </div>
      <div className="landing-table-demo__action landing-table-demo__action--end">
        HAND LIVE · FLOP
      </div>

      {/* Seats: pro avatar + large hole cards */}
      {SEATS.map((s, si) => {
        const pro = CALLING_PROS[s.proIdx] ?? CALLING_PROS[0]!;
        return (
          <div
            key={s.role}
            className={`landing-table-demo__seat landing-table-demo__seat--${s.role.toLowerCase()}`}
            style={{ left: s.left, top: s.top }}
            data-role={s.role}
          >
            <div
              className="landing-table-demo__avatar"
              style={{ ["--si" as string]: si } as CSSProperties}
            >
              <span className="landing-table-demo__ring" />
              <img src={pro.icon} alt="" draggable={false} />
              <span className="landing-table-demo__role">{s.label}</span>
              <span className="landing-table-demo__name">{pro.shortName}</span>
            </div>
            <div className="landing-table-demo__holes">
              {s.hole.map((src, c) => (
                <div
                  key={c}
                  className="landing-table-demo__card landing-table-demo__card--hole"
                  style={
                    {
                      ["--i" as string]: si * 2 + c,
                      ["--c" as string]: c,
                      ["--si" as string]: si,
                      backgroundImage: `url(${src})`,
                    } as CSSProperties
                  }
                />
              ))}
            </div>
            {/* Bet stack under seat on action */}
            <div
              className={`landing-table-demo__bet landing-table-demo__bet--${s.role.toLowerCase()}`}
            >
              <img src="/chips/100.png" alt="" />
            </div>
          </div>
        );
      })}

      <div className="landing-table-demo__ui">
        <span>NL 5/10</span>
        <span className="landing-table-demo__pot-label">Calling Pros · 6-max</span>
        <span>GBUX</span>
      </div>
    </div>
  );
}
